import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import __vue_component__$1 from '../../buttons/ButtonComponent/ButtonComponent.vue.js';
import __vue_component__$4 from '../Header/Header.vue.js';
import __vue_component__$2 from './components/ConfirmLogoutModal.vue.js';
import __vue_component__$3 from './components/DesktopMenu.vue.js';
import __vue_component__$5 from './components/SideMenu.vue.js';
import { useSharedNavBarLogic } from './helper.js';
import __vue_component__ from '../../specific/dev/AdvancedSettingsButton/AdvancedSettingsButton.vue.js';
import __vue_component__$6 from '../../transitions/TransitionFadeInDelay/TransitionFadeInDelay.vue.js';
import __vue_component__$7 from '../../transitions/TransitionFadeOutIn/TransitionFadeOutIn.vue.js';
import __vue_component__$8 from '../../transitions/TransitionRightLeft/TransitionRightLeft.vue.js';
import { useNavBarHeight } from '../../../composables/dom.js';
import '@caresend/utils';
import { getStore } from '../../../store/helpers/initStore.js';
import { useRouteHeading } from '../../../composables/routeHeading.js';
import '@caresend/types';
import 'immutability-helper';
import { getRoute, getRouter } from '../../../router/helpers/initRouter.js';
import '../../../router/routes/dev/model.js';
import '../../../router/routes/dev/routes.js';
import { homeRoute } from '../../../router/routes/locations.js';
import '../../../router/routes/orderFlow/model.js';
import '../../../router/routes/orderFlow/privateHelpers.js';
import '../../../router/routes/orderFlow/routes.js';
import '../../../router/routes/schedulingFlow/model.js';
import '../../../router/routes/schedulingFlow/routes.js';
import '../../../router/routes/chartingFlow/model.js';
import '../../../router/routes/chartingFlow/routes.js';
import '../../../router/routes/portal/portalRoutes.js';
import '../../../router/routes/portal/model.js';
import '../../../router/routes/pro/model.js';
import '../../../router/routes/visitsPage/model.js';
import '../../../router/routes/visitsPage/routes.js';

var script = defineComponent({
    name: 'NavBar',
    components: {
        AdvancedSettingsButton: __vue_component__,
        ButtonComponent: __vue_component__$1,
        ConfirmLogoutModal: __vue_component__$2,
        DesktopMenu: __vue_component__$3,
        Header: __vue_component__$4,
        SideMenu: __vue_component__$5,
        TransitionFadeInDelay: __vue_component__$6,
        TransitionFadeOutIn: __vue_component__$7,
        TransitionRightLeft: __vue_component__$8,
    },
    props: {
        compactNavBar: Boolean,
        includeHomeForLoggedInUser: Boolean,
        logInRoute: {
            type: Object,
            required: true,
        },
        navItemsForLoggedInUser: {
            type: Array,
            default: () => [],
        },
        partnerLogoUrl: {
            type: String,
            default: null,
        },
        partnerName: {
            type: String,
            default: null,
        },
        signUpRoute: {
            type: Object,
            default: null,
        },
        smallLogo: Boolean,
        staticLogo: Boolean,
        useRouteMetaColorOverride: Boolean,
        useCustomMenus: Boolean,
        isApproved: Boolean,
    },
    setup(props) {
        const route = getRoute();
        const router = getRouter();
        const store = getStore();
        const headerContainerTemplateRef = ref(null);
        const { isLoggedIn, hideNavItems, hideSignInButton, hideSignUpButton } = useSharedNavBarLogic();
        const routeMetaColorOverride = computed(() => {
            var _a;
            if (!props.useRouteMetaColorOverride)
                return;
            return (_a = route.value.meta) === null || _a === void 0 ? void 0 : _a.navBarColorOverride;
        });
        const { heading } = useRouteHeading();
        onMounted(() => {
            const { syncContentMarginWithNavBarHeight } = useNavBarHeight();
            syncContentMarginWithNavBarHeight(headerContainerTemplateRef, { isHeader: true });
        });
        const classNames = computed(() => {
            var _a;
            const names = [];
            /** Only conditions which apply changes to navbar bg color */
            if (routeMetaColorOverride.value)
                names.push(`NavBar--bg-${routeMetaColorOverride.value}`);
            else if (isLoggedIn.value)
                names.push('NavBar--bg-white');
            if (props.partnerName)
                names.push('NavBar--with-partner');
            if (props.smallLogo)
                names.push('NavBar--small-logo');
            if (props.compactNavBar)
                names.push('NavBar--compact');
            if ((_a = heading.value) === null || _a === void 0 ? void 0 : _a.disableStickyHeader)
                names.push('NavBar--disable-sticky-header');
            return names;
        });
        const isSideMenuOpened = ref(false);
        const isLogoutModalOpened = ref(false);
        watch(() => { var _a; return (_a = heading.value) === null || _a === void 0 ? void 0 : _a.title; }, (title) => {
            const appTitle = store.getters['app/getAppTitle'];
            const titleText = title ? `${appTitle} - ${title}` : appTitle;
            document.title = titleText;
        }, { immediate: true });
        const loggedOutNavItems = computed(() => [
            ...(props.useCustomMenus && !hideSignInButton.value ? [{
                    label: 'Log in',
                    icon: 'sign-in-alt',
                    to: props.logInRoute,
                }] : []),
            ...(props.signUpRoute && !hideSignUpButton.value ? [{
                    label: 'Sign up',
                    icon: 'user-plus',
                    to: props.signUpRoute,
                }] : []),
        ]);
        const signOutNavItems = computed(() => [
            ...(props.useCustomMenus ? [{
                    label: 'Sign out',
                    icon: 'door-open',
                    keepMenuOpened: true,
                    click: () => { isLogoutModalOpened.value = true; },
                }] : []),
        ]);
        const loggedInNavItems = computed(() => [
            ...(props.includeHomeForLoggedInUser && props.isApproved ? [{
                    label: 'Home',
                    icon: 'home',
                    click: () => router.push(homeRoute()),
                }] : []),
            ...props.navItemsForLoggedInUser,
            ...(props.useCustomMenus ? [{
                    label: 'Sign out',
                    icon: 'door-open',
                    keepMenuOpened: true,
                    click: () => { isLogoutModalOpened.value = true; },
                }] : []),
        ]);
        const navItems = computed(() => {
            if (hideNavItems.value)
                return signOutNavItems.value;
            return isLoggedIn.value
                ? loggedInNavItems.value
                : loggedOutNavItems.value;
        });
        watch(() => navItems.value.length, (hasNavItems) => {
            if (!hasNavItems)
                isSideMenuOpened.value = false;
        });
        return {
            ...useSharedNavBarLogic(),
            classNames,
            headerContainerTemplateRef,
            heading,
            isLogoutModalOpened,
            isSideMenuOpened,
            navItems,
        };
    },
});

export { script as default };
